import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { DEFAULT_APPEARANCE, type StorefrontAppearance, type StorefrontThemeId } from '@/lib/appearanceDefaults';

interface UseStorefrontAppearanceResult {
  appearance: StorefrontAppearance;
  loading: boolean;
  isCustomized: boolean;
  save: (data: Partial<StorefrontAppearance>) => Promise<boolean>;
  reset: () => Promise<boolean>;
  refresh: () => void;
}

export function useStorefrontAppearance(
  userId: string | undefined,
  themeId: StorefrontThemeId = 'padrao',
  initialAppearance?: StorefrontAppearance | null
): UseStorefrontAppearanceResult {
  const hasInitial = !!initialAppearance;
  // Merge over defaults here too — same reason as the fetch path below: a column
  // added after this preloaded row was cached (migration not applied yet, or just
  // rolled out) must fall back to its documented default instead of `undefined`.
  const [appearance, setAppearance] = useState<StorefrontAppearance>(
    initialAppearance ? { ...DEFAULT_APPEARANCE, ...initialAppearance } : DEFAULT_APPEARANCE
  );
  const [loading, setLoading] = useState(!hasInitial);
  const [isCustomized, setIsCustomized] = useState(hasInitial);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (initialAppearance) {
      setAppearance({ ...DEFAULT_APPEARANCE, ...initialAppearance });
      setIsCustomized(true);
      setLoading(false);
    }
  }, [initialAppearance]);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    if (hasInitial && refreshKey === 0) {
      return;
    }

    const fetchAppearance = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from('storefront_appearance')
        .select('*')
        .eq('user_id', userId)
        .eq('theme_id', themeId)
        .maybeSingle();

      if (error) {
        console.error('Error fetching appearance:', error);
        if (!hasInitial) {
          setAppearance({ ...DEFAULT_APPEARANCE, theme_id: themeId });
          setIsCustomized(false);
        }
      } else if (data) {
        // Merge over defaults so a column added after this row was created
        // (migration not applied yet, or just rolled out) never shows up as
        // `undefined` in a control — it falls back to its documented default.
        setAppearance({ ...DEFAULT_APPEARANCE, ...(data as StorefrontAppearance) });
        setIsCustomized(true);
      } else {
        setAppearance({ ...DEFAULT_APPEARANCE, theme_id: themeId });
        setIsCustomized(false);
      }
      setLoading(false);
    };

    fetchAppearance();
  }, [userId, themeId, refreshKey]);

  const save = useCallback(async (data: Partial<StorefrontAppearance>): Promise<boolean> => {
    if (!userId) return false;

    const payload = { ...data, user_id: userId, theme_id: themeId, updated_at: new Date().toISOString() };
    delete payload.id;

    if (isCustomized) {
      const { error } = await supabase
        .from('storefront_appearance')
        .update(payload)
        .eq('user_id', userId)
        .eq('theme_id', themeId);
      if (error) {
        console.error('Error updating appearance:', error);
        return false;
      }
    } else {
      const { error } = await supabase
        .from('storefront_appearance')
        .insert(payload);
      if (error) {
        console.error('Error inserting appearance:', error);
        return false;
      }
      setIsCustomized(true);
    }

    setAppearance(prev => ({ ...prev, ...data }));
    return true;
  }, [userId, themeId, isCustomized]);

  const reset = useCallback(async (): Promise<boolean> => {
    if (!userId) return false;

    const resetData = { ...DEFAULT_APPEARANCE, user_id: userId, theme_id: themeId, updated_at: new Date().toISOString() };

    if (isCustomized) {
      const { error } = await supabase
        .from('storefront_appearance')
        .update(resetData)
        .eq('user_id', userId)
        .eq('theme_id', themeId);
      if (error) {
        console.error('Error resetting appearance:', error);
        return false;
      }
    }

    setAppearance({ ...DEFAULT_APPEARANCE, theme_id: themeId });
    return true;
  }, [userId, themeId, isCustomized]);

  const refresh = useCallback(() => setRefreshKey(k => k + 1), []);

  return { appearance, loading, isCustomized, save, reset, refresh };
}
